import React from "react";
import { Banner } from "@/types/banner";
import { useIsMobile } from "@/hooks/use-mobile";

interface PageBannerProps {
  banners: Banner[];
}

export const PageBanner: React.FC<PageBannerProps> = ({ banners }) => {
  const isMobile = useIsMobile();

  console.log("🎯 [PageBanner] Renderizando com banners:", banners?.length || 0);
  console.log("🎯 [PageBanner] Banners data:", banners);

  if (!banners || banners.length === 0) {
    console.log("🚫 [PageBanner] Sem banners, retornando null");
    return null;
  }

  // Detectar a cor de fundo da página atual
  const getPageBackgroundColor = () => {
    const path = window.location.pathname;
    // Comunidades usa #0A0F16, outras páginas usam #0f141c
    return path.includes('/comunidades') ? '#0A0F16' : '#0f141c';
  };

  // Função para detectar se é link externo
  const isExternalLink = (url: string) => {
    // Se já tem protocolo http/https, é externo
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return true;
    }
    
    // Se começa com /, é link interno
    if (url.startsWith('/')) {
      return false;
    }
    
    // Se contém ponto e não começa com /, provavelmente é domínio externo
    if (url.includes('.') && !url.startsWith('/')) {
      return true;
    }
    
    // Caso contrário, é link interno
    return false;
  };

  // Função para formatar URL externa
  const formatExternalUrl = (url: string) => {
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    // Adiciona https:// se não tiver protocolo
    return `https://${url}`;
  };

  // Função para gerar estilos da imagem com posicionamento
  const getImageStyles = (banner: Banner) => {
    const baseStyles = {
      minHeight: isMobile ? "200px" : "300px",
      maxHeight: isMobile ? "300px" : "400px"
    };

    console.log(`🎨 [getImageStyles] Banner ${banner.title}:`, {
      isMobile,
      desktop_image_position: banner.desktop_image_position,
      desktop_image_object_position: banner.desktop_image_object_position,
      desktop_image_scale: banner.desktop_image_scale
    });
    
    console.log(`🔍 [getImageStyles] Valores em bruto:`, {
      desktop_image_position: banner.desktop_image_position,
      desktop_image_object_position: banner.desktop_image_object_position,
      desktop_image_scale: banner.desktop_image_scale,
      tipo_position: typeof banner.desktop_image_position,
      tipo_object_position: typeof banner.desktop_image_object_position,
      tipo_scale: typeof banner.desktop_image_scale
    });

    // Se for mobile, usar configurações padrão
    if (isMobile) {
      console.log(`📱 [getImageStyles] Mobile detected, using base styles`);
      return baseStyles;
    }
    
    console.log(`💻 [getImageStyles] Desktop mode - aplicando configurações de posicionamento`);

    // Para desktop, aplicar configurações de posicionamento
    const additionalStyles: React.CSSProperties = {};

    if (banner.desktop_image_object_position) {
      additionalStyles.objectPosition = banner.desktop_image_object_position;
      console.log(`🎯 [getImageStyles] Applied objectPosition: ${banner.desktop_image_object_position}`);
    } else {
      console.log(`❌ [getImageStyles] desktop_image_object_position está vazio/null/undefined`);
    }

    if (banner.desktop_image_scale && banner.desktop_image_scale !== 1) {
      additionalStyles.transform = `scale(${banner.desktop_image_scale})`;
      additionalStyles.transformOrigin = banner.desktop_image_object_position || 'center center';
      console.log(`🔍 [getImageStyles] Applied scale: ${banner.desktop_image_scale}, origin: ${additionalStyles.transformOrigin}`);
    }

    const finalStyles = {
      ...baseStyles,
      ...additionalStyles
    };

    console.log(`✅ [getImageStyles] Final styles:`, finalStyles);
    return finalStyles;
  };

  console.log("✅ [PageBanner] Renderizando banners com gradiente aplicado");

  return (
    <div className="w-screen relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] mb-8">
      {banners.map((banner, index) => {
        console.log(`🖼️ [PageBanner] Renderizando banner ${index + 1}:`, {
          id: banner.id,
          title: banner.title,
          hasLink: !!banner.link_url,
          linkUrl: banner.link_url,
          desktopImagePosition: banner.desktop_image_position,
          desktopImageObjectPosition: banner.desktop_image_object_position,
          desktopImageScale: banner.desktop_image_scale
        });

        const imageStyles = getImageStyles(banner);

        return (
          <div
            key={banner.id}
            className={`relative w-full overflow-hidden ${
              index > 0 ? "mt-4" : ""
            }`}
          >
            {/* Gradiente sobreposto para consistência visual */}
            <div 
              className="absolute inset-0 bg-gradient-to-b from-transparent via-[#18222F]/50 z-10 pointer-events-none"
              style={{
                background: `linear-gradient(to bottom, transparent, rgba(24, 34, 47, 0.5), ${getPageBackgroundColor()})`
              }}
            ></div>
            
            {/* Banner Clicável */}
            {banner.link_url ? (
              isExternalLink(banner.link_url) ? (
                <a
                  href={formatExternalUrl(banner.link_url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full cursor-pointer relative z-20"
                >
                  <img
                    src={isMobile && banner.mobile_image_url ? banner.mobile_image_url : banner.image_url}
                    alt={banner.title}
                    className="w-full h-auto object-cover"
                    style={imageStyles}
                  />
                </a>
              ) : (
                <a
                  href={banner.link_url}
                  className="block w-full cursor-pointer relative z-20"
                >
                  <img
                    src={isMobile && banner.mobile_image_url ? banner.mobile_image_url : banner.image_url}
                    alt={banner.title}
                    className="w-full h-auto object-cover"
                    style={imageStyles}
                  />
                </a>
              )
            ) : (
              // Banner sem link
              <img
                src={isMobile && banner.mobile_image_url ? banner.mobile_image_url : banner.image_url}
                alt={banner.title}
                className="w-full h-auto object-cover"
                style={imageStyles}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}; 